import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";

import AutocompleteId from "@/components/Autocomplete/AutocompleteId.vue";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";
import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientAutocomplete } from "@/models/Client";

import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { TOAST_OPTIONS } from "@/constants";

import { buildFormClientField } from "@/models/ClientField";

import { ClientField } from "@/store/modules/clientFields";

export default defineComponent({
  name: "clientFieldEditForm",
  components: {
    FormErrors,
    AutocompleteId,
    LoadingAnimation,
  },
  props: {
    clientFieldId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);

    let deleteModal: any = ref(null);

    const deleting = ref(false);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleteClientField = () => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.clientFieldId,
      };
      $store.dispatch("deleteClientField", jsConn).then(() => {
        ctx.root.$router.push({
          name: NamedRts.clientFields,
        });
        return Promise.resolve();
      });
    };

    const cancelParams = () => {
      if (props.clientFieldId === "new") {
        return {
          name: NamedRts.clientFields,
        };
      } else {
        return {
          name: NamedRts.clientFieldShow,
          params: {
            clientFieldId: props.clientFieldId,
          },
        };
      }
    };

    let { formClientField, loadClientFieldIntoForm } = buildFormClientField();

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    let { updateCommodity } = buildFormCommodityAutocomplete();
    let { updateClient } = buildFormClientAutocomplete();

    const localUpdateCommodity = (evt: AutocompleteResult) => {
      updateCommodity(evt, formClientField.formCommodity, formClientField);
    };

    const localUpdateClient = (evt: AutocompleteResult) => {
      updateClient(evt, formClientField.formClient, formClientField);
    };

    const commoditiesForAutocomplete = computed(() => {
      return $store.getters.commoditiesForAutocompleteId;
    });

    const clientsForAutocomplete = computed(() => {
      return $store.getters.clientsForAutocompleteId;
    });

    const apiClientField = computed(() => {
      return $store.getters.clientField as ClientField;
    });

    onMounted(() => {
      const jsConnCommodities = new JSConn();
      const jsConnClients = new JSConn();
      const jsConnClientField = new JSConn();

      if (props.clientFieldId === "new") {
        jsConnClients.formParams = {
          statuses: ["active"],
        };
      }

      let dataPromises = [
        $store.dispatch("needCommodities", jsConnCommodities),
        $store.dispatch("needClients", jsConnClients),
      ];

      if (props.clientFieldId !== "new") {
        jsConnClientField.formParams = {
          id: props.clientFieldId,
        };
        dataPromises.push(
          $store.dispatch("needClientField", jsConnClientField),
        );
      }

      Promise.all(dataPromises)
        .then(() => {
          // if the form is not a new form,
          // then the data needs to be loaded into the store
          if (props.clientFieldId !== "new") {
            loadClientFieldIntoForm(apiClientField.value, formClientField);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;
      if (props.clientFieldId === "new") {
        formClientField.id = uuidv4();
        jsConn.formParams = formClientField;
        storeDispatch = "createClientField";
      } else {
        jsConn.formParams = formClientField;
        storeDispatch = "updateClientField";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(() => {
          ctx.root.$bvToast.toast(
            "Field was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
        })
        .catch(() => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      formClientField,

      commoditiesForAutocomplete,
      localUpdateCommodity,

      clientsForAutocomplete,
      localUpdateClient,

      onSubmit,
      submitting,
      submitButtonText,

      hasFormErrors,
      formErrors,

      firstInput,
      cancelParams,

      deleting,
      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteClientField,
    };
  },
});
